import capitalize from "lodash/capitalize";
import type { IntlShape } from "react-intl";

import { labelMessages } from "../../priceSummaryLabels.messages";
import { getRoomTypeLabel } from "../../priceSummaryRoomTypes.messages";
import { LabelIds } from "../consts";
import { type LabelArg, type LabelRef, type MoneyArg } from "../types";

const isMoneyArg = (arg: LabelArg): arg is MoneyArg =>
  typeof arg === "object" && arg != null && "money" in arg;

const isLabelRef = (arg: LabelArg): arg is LabelRef =>
  typeof arg === "object" && arg != null && "id" in arg && !("money" in arg);

const formatRoomTypeDisplay = ({
  intl,
  roomType,
}: {
  intl: IntlShape;
  roomType: unknown;
}): string => {
  if (typeof roomType !== "string" || roomType.length === 0) {
    return capitalize(getRoomTypeLabel(null, intl));
  }

  if (roomType === "private_dining") {
    return intl.formatMessage(labelMessages.PrivateDiningRoom);
  }

  return capitalize(getRoomTypeLabel(roomType, intl));
};

/**
 * Resolves a `LabelRef` (semantic label id + args) into localized display text.
 *
 * The core model never carries copy — rows hold `{ id, args }` refs so it stays
 * host-agnostic — so this is the single place a ref becomes user-visible text:
 * `ref.id` picks the `labelMessages` descriptor, `ref.args` fill its
 * placeholders.
 *
 * Args are resolved first: a `MoneyArg` runs through the currency formatter so
 * embedded amounts match the value column, and a nested `LabelRef` recurses so
 * labels can compose. Ids whose message needs a typed or derived placeholder
 * (dates, percents, room types, back-end-provided copy) are handled explicitly
 * below; every other id just formats its descriptor. An id with no descriptor
 * returns the raw id rather than throwing, so a missing message degrades to a
 * visible marker instead of a blank row.
 */
export const formatLabelRef = ({
  currency,
  intl,
  ref,
}: {
  currency?: string;
  intl: IntlShape;
  ref: LabelRef;
}): string => {
  const resolvedArgs = (ref.args ?? []).map((arg) => {
    if (isMoneyArg(arg)) {
      return intl.formatNumber(arg.money, {
        style: "currency",
        currency: currency ?? "USD",
      });
    }
    if (isLabelRef(arg)) {
      return formatLabelRef({ currency, intl, ref: arg });
    }
    return arg;
  });

  if (
    ref.id === LabelIds.AttendeeRange ||
    ref.id === LabelIds.AttendeeRangeReadout
  ) {
    const [min, max] = resolvedArgs;
    return intl.formatMessage(labelMessages[ref.id], {
      min: typeof min === "number" ? min : 0,
      max: typeof max === "number" ? max : 0,
    });
  }

  if (ref.id === LabelIds.DiySpaceRental) {
    const [hours] = resolvedArgs;
    return intl.formatMessage(labelMessages.DiySpaceRental, {
      hours: typeof hours === "number" || typeof hours === "string" ? hours : 0,
    });
  }

  if (ref.id === LabelIds.DiySpaceRentalRate) {
    const [price, hours] = resolvedArgs;
    return intl.formatMessage(labelMessages.DiySpaceRentalRate, {
      price: typeof price === "string" ? price : "",
      hours: typeof hours === "number" || typeof hours === "string" ? hours : 0,
    });
  }

  if (ref.id === LabelIds.FsvSpaceRental || ref.id === LabelIds.RoomTypeHours) {
    const [roomType, hours] = ref.args ?? [];
    return intl.formatMessage(labelMessages.FsvSpaceRental, {
      roomType: formatRoomTypeDisplay({ intl, roomType }),
      hours: typeof hours === "number" || typeof hours === "string" ? hours : 0,
    });
  }

  if (ref.id === LabelIds.DayRateDiscount) {
    const [percent] = resolvedArgs;
    return intl.formatMessage(labelMessages.DayRateDiscount, {
      percent: typeof percent === "number" ? percent : 0,
    });
  }

  if (ref.id === LabelIds.StaffGratuityPercent) {
    const [rate] = resolvedArgs;
    const value = typeof rate === "number" ? rate : 0;
    return intl.formatMessage(labelMessages.StaffGratuityPercent, {
      percent: intl.formatNumber(value, {
        style: "percent",
        maximumFractionDigits: 1,
      }),
    });
  }

  if (ref.id === LabelIds.FoodDrinkMinimum) {
    const foodDrinkType = resolvedArgs[0];
    switch (foodDrinkType) {
      case "food":
        return intl.formatMessage(labelMessages.FoodMinimum);
      case "drink":
        return intl.formatMessage(labelMessages.DrinkMinimum);
      case "food_and_drink":
      default:
        return intl.formatMessage(labelMessages.FoodDrinkMinimum);
    }
  }

  if (ref.id === LabelIds.Entitlement) {
    const [beLabel] = resolvedArgs;
    if (typeof beLabel === "string" && beLabel.length > 0) {
      return beLabel;
    }
    return intl.formatMessage(labelMessages.Entitlement);
  }

  if (ref.id === LabelIds.EntitlementCaption) {
    const [beCaption] = resolvedArgs;
    return typeof beCaption === "string" ? beCaption : "";
  }

  if (ref.id === LabelIds.TerminalBooking) {
    const [state] = resolvedArgs;
    return intl.formatMessage(labelMessages.TerminalBooking, {
      state: typeof state === "string" ? state : "",
    });
  }

  if (ref.id === LabelIds.UpcomingChargeDate || ref.id === LabelIds.EventDate) {
    const [rawDate] = resolvedArgs;
    if (typeof rawDate !== "string" && !(rawDate instanceof Date)) {
      return "";
    }
    const date = typeof rawDate === "string" ? new Date(rawDate) : rawDate;
    if (Number.isNaN(date.getTime())) {
      return typeof rawDate === "string" ? rawDate : "";
    }
    return intl.formatMessage(labelMessages[ref.id], {
      date: intl.formatDate(date, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    });
  }

  if (ref.id === LabelIds.IncludesDueAtVenue) {
    const [amount] = resolvedArgs;
    // MoneyArg is pre-formatted by the shared money path; raw numbers still format here.
    const formattedAmount =
      typeof amount === "string"
        ? amount
        : intl.formatNumber(typeof amount === "number" ? amount : 0, {
            style: "currency",
            currency: currency ?? "USD",
          });
    return intl.formatMessage(labelMessages.IncludesDueAtVenue, {
      amount: formattedAmount,
    });
  }

  if (ref.id === LabelIds.AddOnUnitPrice) {
    const [price, unit] = resolvedArgs;
    return intl.formatMessage(labelMessages.AddOnUnitPrice, {
      price: typeof price === "string" ? price : "",
      unit: typeof unit === "string" ? unit : "",
    });
  }

  if (ref.id === LabelIds.AddOnUnitPriceOnly) {
    const [price] = resolvedArgs;
    return intl.formatMessage(labelMessages.AddOnUnitPriceOnly, {
      price: typeof price === "string" ? price : "",
    });
  }

  const descriptor = labelMessages[ref.id as keyof typeof labelMessages];
  if (!descriptor) {
    return ref.id;
  }

  return intl.formatMessage(descriptor);
};
